// Global Instructions Rule Applied!
//
// "Where do I add a custom connector?" — one block per AI assistant.
//
// Registered in ui_configs/helpSections.ts under component_key
// 'agent_client_guides' and rendered by the help drawer inside the guide's
// "Step 2" section.
//
// Everything shown here comes from cfg.agent_client_guides via
// globalConfigs.agent_access.connection.clients. That is the whole point:
// vendor menu paths and plan gating change without warning, and supporting a
// third assistant should be an INSERT, not a release. Nothing in this file
// names a vendor.

import React, { useMemo } from 'react'
import { Empty, Typography } from 'antd'
import { useSelector } from 'react-redux'
import { ShadowRichHtml } from '../../../core/components/ShadowRichHtml.tsx'
import { HELP_ARTICLE_CSS } from '../../../core/components/HelpDrawer/helpArticleCss.ts'
import { AgentAccessConfigSchema } from '../../../state_manager/data_models/agent_access_model.ts'
import type { GlobalConfigsState } from '../../../state_manager/interfaces/global_configs.ts'

const { Text } = Typography

export function AgentClientGuides() {
  const rawAgentAccess = useSelector(
    (state: { globalConfigs: GlobalConfigsState }) => state.globalConfigs?.agent_access
  )
  // Parse-with-defaults, matching MyAgentsTab: a missing or partial cfg payload
  // degrades to an empty list rather than throwing inside the drawer.
  const agentAccess = useMemo(
    () => AgentAccessConfigSchema.parse(rawAgentAccess ?? {}),
    [rawAgentAccess]
  )

  const clients = agentAccess.connection.clients

  if (clients.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description='No assistant instructions have been configured yet.'
      />
    )
  }

  return (
    <div className='flex flex-col gap-2'>
      {clients.map((client) => (
        <div
          key={client.client_key}
          className='rounded border border-gray-200 p-3'
          data-debug={`agent-client-guide-${client.client_key}`}
        >
          <div className='mb-2 flex items-center justify-between gap-2'>
            <Text strong className='text-sm'>
              {client.name || client.client_key}
            </Text>
            {client.doc_url ? (
              <a href={client.doc_url} target='_blank' rel='noopener noreferrer' className='text-xs'>
                Vendor docs
              </a>
            ) : null}
          </div>

          {client.path_hint ? (
            <div className='mb-2 rounded border border-gray-200 bg-gray-50 px-2 py-1 font-mono text-xs text-gray-700'>
              {client.path_hint}
            </div>
          ) : null}

          {client.steps_html ? (
            <ShadowRichHtml html={client.steps_html} css={HELP_ARTICLE_CSS} />
          ) : null}
          {client.notes_html ? (
            <ShadowRichHtml html={client.notes_html} css={HELP_ARTICLE_CSS} />
          ) : null}
        </div>
      ))}
    </div>
  )
}
